import { memo, useCallback, useMemo, useRef, useState } from "react";
import { Box, Divider, FormControlLabel, Menu, Radio, RadioGroup, Tooltip, Typography, useTheme } from "@mui/material";
import { ExtraSmallButton } from "../../../components/custom";
import Settings from "../../../assets/icons/Settings";
import SettingsOutlinedIcon from "../../../assets/icons/SettingOutlined";
import { MULTIPANE_FULLSCREEN_MODE_ID } from "../../../assets/constants";
import { useSelector } from "react-redux";
import { selectMultiPaneFullScreen } from "../../../selectors/common";
import SlTargetInput, { SlTargetType } from "../../../components/common/slTargetInput/slTargetInput";
import { selectScalperTradeOptions } from "../../../selectors/scalper";
import { useDispatch } from "react-redux";
import { setTradeOptions } from "../../../reducer/scalper.reducer";
import { Broker_OrderType, OrderType, Product } from "../../../proto/generated/order";
import NumberInputField from "../../../components/common/numberInputField/numberInputField";

const ScalperSettingsMenu: React.FC = () => {
    const dispatch = useDispatch();
    const buttonRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();
    const container = useSelector(selectMultiPaneFullScreen)
    const tradeOptions = useSelector(selectScalperTradeOptions);

    const handleOpenPopover = useCallback((event: any) => {
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClosePopover = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const open = useMemo(() => Boolean(anchorEl), [anchorEl])

    const onSlChange = useCallback((value: number, valueType: SlTargetType) => {
        dispatch(setTradeOptions({ stopLoss: value, stopLossType: valueType }))
    }, [])

    const onTargetChange = useCallback((value: number, valueType: SlTargetType) => {
        dispatch(setTradeOptions({ target: value, targetType: valueType }))
    }, [])

    const onProductChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const orderProduct = (event.target as HTMLInputElement).value as 'MIS' | 'NRML' | 'CNC';
        dispatch(setTradeOptions({ orderProduct }))
    }, [])

    const onOrderTypeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const orderType = (event.target as HTMLInputElement).value as unknown as OrderType;
        dispatch(setTradeOptions({ orderType }))
    }, [])

    const onMultiplierChange = useCallback((value: number) => {
        dispatch(setTradeOptions({ quantityMultiplier: value }))
    }, [])

    return <div><Tooltip title="Scalper Settings" arrow placement='bottom'>
        <ExtraSmallButton
            id={'scalper-layout-settings-open-button'}
            size="small"
            ref={buttonRef}
            color="inherit"
            onClick={handleOpenPopover}
            data-event={'Scalper Layout Button Clicked'}
            data-event-source={'stockbar'}
        >
            <SettingsOutlinedIcon sx={{ width: 20, height: 20 }} />
        </ExtraSmallButton>
    </Tooltip>
        {open && <Menu open={open} anchorEl={anchorEl}
            container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center'
            }} onClose={handleClosePopover}
            slotProps={{
                paper: {
                    id: 'scalper-settings-menu',
                    sx: { width: 260, maxWidth: 260 },
                }
            }}
        >
            <Box id={'multipane-menu-layout'} sx={{ display: 'flex', flexDirection: 'column', paddingBottom: 1, width: '100%', boxSizing: 'border-box' }}>

                <Typography variant={'caption'} color="textSecondary" sx={{ p: theme.spacing(1, 2), textTransform: "uppercase" }}>QUANTITY MULTIPLER</Typography>
                <Box sx={{ p: theme.spacing(0, 2), width: '70%' }} >
                    <NumberInputField onValueChange={onMultiplierChange} minValue={1} height={20} value={tradeOptions.quantityMultiplier ?? 1} />
                </Box>
                <Divider sx={{ margin: theme.spacing(2, 0, 1) }} />
                <Typography variant={'caption'} color="textSecondary" sx={{ p: theme.spacing(1, 2, 0), textTransform: "uppercase" }}>PRODUCT</Typography>
                <RadioGroup
                    row
                    value={tradeOptions?.orderProduct}
                    onChange={onProductChange}
                    sx={{ marginX: 2 }}
                >
                    <FormControlLabel
                        style={{ marginRight: 15, width: '50%' }}
                        value={Product.INTRADAY}
                        control={<Radio size="small" />}
                        label={<Typography color='textPrimary' variant="body2">Intraday</Typography>}
                    />
                    <FormControlLabel
                        style={{ marginRight: 0, width: '50%' }}
                        value={Product.CNC}
                        control={<Radio size="small" />}
                        label={<Typography color='textPrimary' variant="body2">Overnight </Typography>}
                    />
                </RadioGroup>
                <Divider sx={{ margin: theme.spacing(2, 0, 1) }} />
                <Typography variant={'caption'} color="textSecondary" sx={{ p: theme.spacing(1, 2, 0), textTransform: "uppercase" }}>ORDER TYPE</Typography>
                <RadioGroup
                    row
                    value={tradeOptions?.orderType}
                    onChange={onOrderTypeChange}
                    sx={{ marginX: 2, marginY: 0 }}
                >
                    <FormControlLabel
                        style={{ marginRight: 15, width: '50%' }}
                        value={Broker_OrderType.MARKET_ORDER}
                        control={<Radio size="small" />}
                        label={<Typography color='textPrimary' variant="body2">MARKET</Typography>}
                    />
                    <FormControlLabel
                        style={{ marginRight: 0, width: '50%' }}
                        value={Broker_OrderType.LIMIT_ORDER}
                        control={<Radio size="small" />}
                        label={<Typography color='textPrimary' variant="body2">LIMIT</Typography>}
                    />
                </RadioGroup>

                <Divider sx={{ margin: theme.spacing(2, 0, 1) }} />
                <Box sx={{ display: 'flex', gap: 1, px: 2, alignItems: 'flex-start', width: '100%', boxSizing: 'border-box' }}>
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Typography variant={'caption'} color="textSecondary" sx={{ pb: 0.5, display: 'block', textTransform: "uppercase" }}>STOP LOSS</Typography>
                        <SlTargetInput value={tradeOptions?.stopLoss} valueType={tradeOptions?.stopLossType} onValueChange={onSlChange} height={24} fullWidth />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Typography variant={'caption'} color="textSecondary" sx={{ pb: 0.5, display: 'block', textTransform: "uppercase" }}>TARGET</Typography>
                        <SlTargetInput value={tradeOptions?.target} valueType={tradeOptions?.targetType} onValueChange={onTargetChange} height={24} fullWidth />
                    </Box>
                </Box>

            </Box>
        </Menu>}
    </div>;
}

export default memo(ScalperSettingsMenu);